/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 31/08/2025 - 14:23:44
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 31/08/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  GroupWork as GroupWorkIcon,
  Build as BuildIcon,
  Bolt as BoltIcon,
  Construction as ConstructionIcon,
  VerifiedUser as VerifiedUserIcon,
  Code as CodeIcon,
  DeviceHub as DeviceHubIcon,
  LocalShipping as LocalShippingIcon,
  Storage as StorageIcon,
  SupportAgent as SupportAgentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  BugReport as BugReportIcon,
  Speed as SpeedIcon,
  SettingsSystemDaydream as SettingsSystemDaydreamIcon,
  PersonSearch as PersonSearchIcon,
  Analytics as AnalyticsIcon,
  AccessTime as AccessTimeIcon,
  Cloud as CloudIcon,
  People as PeopleIcon,
  Widgets as WidgetsIcon,
  Apartment as ApartmentIcon,
  Handshake as HandshakeIcon,
  Psychology as PsychologyIcon,
  Assessment as AssessmentIcon,
  Architecture as ArchitectureIcon,
  PlayArrow as PlayArrowIcon,
  ManageAccounts as ManageAccountsIcon,
  CheckCircleOutline as CheckCircleIcon,
  Loop as LoopIcon,
  Devices as DevicesIcon,
  Cached as CachedIcon,
  Dashboard as DashboardIcon,
  RocketLaunch as RocketLaunchIcon,
  AssuredWorkload as AssuredWorkloadIcon,
  Engineering as EngineeringIcon,
  ThumbUp as ThumbUpIcon,
  East as EastIcon
} from '@mui/icons-material';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import PageTitle from '../../components/PageTitle';
import LetsTalk from '../../components/letsTalk';

// Define types for our state and data structures
interface VisibilityState {
  hero: boolean;
  overview: boolean;
  features: boolean;
  process: boolean;
  technologies: boolean;
  cta: boolean;
  testimonials: boolean;
}

interface SectionRefs {
  hero: React.RefObject<HTMLElement>;
  overview: React.RefObject<HTMLElement>;
  features: React.RefObject<HTMLElement>;
  process: React.RefObject<HTMLElement>;
  technologies: React.RefObject<HTMLElement>;
  cta: React.RefObject<HTMLElement>;
  testimonials: React.RefObject<HTMLElement>;
}

interface PortfolioProject {
  id: number;
  title: string;
  description: string;
  image: string;
  tags: string[];
}

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
  category: string;
}

interface ProcessStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface Technology {
  name: string;
  icon: React.ReactNode;
  delay: number;
  category: string;
}

interface Testimonial {
  id: number;
  content: string;
  author: string;
  position: string;
  image: string;
  rating: number;
}

// Custom styled components
const HeroBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#044D76', // Maintaining the same color scheme
  color: theme.palette.common.white,
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(16, 0),
  },
}));

const SupportBg = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  overflow: 'hidden',
}));

const GradientCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
}));

const FadeInBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const FeatureCard = styled(Paper)<{ visible: boolean; delay?: number; category?: string }>(({ theme, visible, delay = 0, category }) => ({
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  backgroundColor: '#ffffff',
  borderTop: category ? `4px solid ${getCategoryColor(category)}` : 'none',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
    '& .featureIcon': {
      transform: 'scale(1.1)',
    },
    '& .featureTitle': {
      color: '#044D76', // Consistent with the consultation page
    },
  },
}));

// Function to get color based on category
function getCategoryColor(category: string): string {
  switch(category) {
    case 'Agile':
      return '#0099ff';
    case 'Product':
      return '#044D76';
    case 'QA':
      return '#38b000';
    case 'Offshore':
      return '#9c27b0';
    default:
      return '#044D76';
  }
}

const IconBox = styled(Box)(({ theme }) => ({
  transition: 'transform 0.3s ease',
  marginBottom: theme.spacing(2),
}));

const TechCard = styled(Paper)<{ visible: boolean; delay?: number; category?: string }>(({ theme, visible, delay = 0, category }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: '12px',
  textAlign: 'center',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 153, 255, 0.1)',
  borderLeft: category ? `4px solid ${getCategoryColor(category)}` : 'none',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
    '& .techIcon': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(4, 77, 118, 0.15)',
    }
  },
}));

const ProcessTimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  height: '100%',
  width: '2px',
  backgroundColor: 'rgba(4, 77, 118, 0.2)',
  opacity: 0.5,
}));

const ProcessStepBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const ProcessIconBox = styled(Box)(({ theme }) => ({
  zIndex: 10,
  flexShrink: 0,
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#044D76',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[5],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  borderRadius: '12px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[10],
    '& .projectImage': {
      transform: 'scale(1.1)',
    },
    '& .projectOverlay': {
      opacity: 1,
    },
  },
}));

const ProjectImage = styled('img')({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
});

const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(to top, ${theme.palette.common.black}cc, ${theme.palette.common.black}66, transparent)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: theme.spacing(3),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const TechIconBox = styled(Box)<{ category?: string }>(({ theme, category }) => ({
  height: 64,
  width: 64,
  borderRadius: '50%',
  backgroundColor: category ? `${alpha(getCategoryColor(category), 0.1)}` : 'rgba(4, 77, 118, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  transition: 'all 0.3s ease',
}));

const KanbanBoard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-5px)',
  },
}));

const KanbanHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#044D76',
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: theme.palette.common.white,
}));

const KanbanColumns = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '16px',
  gap: '16px',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(4, 77, 118, 0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(4, 77, 118, 0.05)',
  },
}));

const KanbanColumn = styled(Box)(({ theme }) => ({
  minWidth: '250px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
}));

const KanbanCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '8px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
  },
}));

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.common.white,
  position: 'relative',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 30,
    left: 30,
    width: 60,
    height: 60,
    borderRadius: '50% 0 50% 50%',
    backgroundColor: 'rgba(4, 77, 118, 0.1)',
    zIndex: 0
  }
}));

const TestimonialNavButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#044D76',
  border: '2px solid rgba(4, 77, 118, 0.2)',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(4, 77, 118, 0.1)'
  }
}));

const CategoryChip = styled(Chip)<{ category: string }>(({ theme, category }) => ({
  backgroundColor: alpha(getCategoryColor(category), 0.1),
  color: getCategoryColor(category),
  fontWeight: 'bold',
  borderRadius: '4px',
  marginBottom: theme.spacing(2),
}));

const SupportMaintenancePage: React.FC = () => {
  const theme = useTheme();
  
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    hero: false,
    overview: false,
    features: false,
    process: false,
    technologies: false,
    cta: false,
    testimonials: false
  });
  
  // Initialize the refs with proper typing
  const sectionRefs: SectionRefs = {
    hero: useRef<HTMLElement>(null),
    overview: useRef<HTMLElement>(null),
    features: useRef<HTMLElement>(null),
    process: useRef<HTMLElement>(null),
    technologies: useRef<HTMLElement>(null),
    cta: useRef<HTMLElement>(null),
    testimonials: useRef<HTMLElement>(null)
  };
  
  // Track when sections come into view
  useEffect(() => {
    const observers: Record<string, IntersectionObserver> = {};
    
    Object.entries(sectionRefs).forEach(([section, ref]) => {
      observers[section] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        },
        { threshold: 0.1 }
      );
      
      if (ref.current) {
        observers[section].observe(ref.current);
      }
    });
    
    return () => {
      Object.entries(observers).forEach(([section, observer]) => {
        if (sectionRefs[section as keyof SectionRefs].current) {
          observer.unobserve(sectionRefs[section as keyof SectionRefs].current!);
        }
      });
    };
  }, []);
  
  const handleContactClick = (): void => {
    console.log('Contact us clicked');
    // Add navigation logic here
  };
  
  // Sample portfolio projects
  const portfolioProjects: PortfolioProject[] = [
    {
      id: 1,
      title: 'Enterprise Software Maintenance',
      description: 'Provided ongoing maintenance and support for a global logistics company, reducing system downtime by 78% and improving response time by 45%.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Enterprise+Maintenance',
      tags: ['Software Maintenance', 'Agile', 'DevOps', 'Enterprise']
    },
    {
      id: 2,
      title: 'Agile Transformation Program',
      description: 'Implemented Agile methodologies for a financial services firm, resulting in 40% faster delivery cycles and improved stakeholder satisfaction.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Agile+Transformation',
      tags: ['Agile Implementation', 'Scrum', 'Project Management', 'Financial']
    },
    {
      id: 3,
      title: 'QA Process Automation',
      description: 'Established automated testing infrastructure for an e-commerce platform, reducing testing time by 85% while increasing test coverage by 60%.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=QA+Automation',
      tags: ['Quality Assurance', 'Test Automation', 'CI/CD', 'E-commerce']
    },
    {
      id: 4,
      title: 'Offshore Development Team',
      description: 'Built and managed an offshore development team for a healthcare tech company, delivering ongoing development and support at 40% cost reduction.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Offshore+Team',
      tags: ['Offshore Staffing', 'Team Management', 'Healthcare', 'Development']
    }
  ];

  // Feature items
  const featureItems: FeatureItem[] = [
    // Agile Project Management
    {
      icon: <LoopIcon fontSize="large" sx={{ color: '#0099ff' }} />,
      title: "Agile Methodology Implementation",
      description: "Transform your development processes with tailored Agile methodologies including Scrum, Kanban, and SAFe, complete with team training and coaching.",
      delay: 0,
      category: "Agile"
    },
    {
      icon: <DashboardIcon fontSize="large" sx={{ color: '#0099ff' }} />,
      title: "Sprint Planning & Execution",
      description: "Expert facilitation of sprint planning, daily stand-ups, reviews, and retrospectives to ensure consistent project velocity and continuous improvement.",
      delay: 100,
      category: "Agile"
    },
    {
      icon: <AssessmentIcon fontSize="large" sx={{ color: '#0099ff' }} />,
      title: "Agile Metrics & Reporting",
      description: "Comprehensive tracking and analysis of key performance indicators to measure team productivity, project health, and identify optimization opportunities.",
      delay: 200,
      category: "Agile"
    },
    
    // Product Management
    {
      icon: <RocketLaunchIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Product Roadmap Development",
      description: "Strategic product planning that aligns business goals with user needs, creating clear roadmaps that prioritize features and guide development efforts.",
      delay: 300,
      category: "Product"
    },
    {
      icon: <GroupWorkIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Backlog Management",
      description: "Structured approach to requirement gathering, user story creation, and backlog refinement that ensures development teams always work on highest-value items.",
      delay: 400,
      category: "Product"
    },
    {
      icon: <AnalyticsIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Product Analytics & Optimization",
      description: "Data-driven product decisions through user behavior analysis, A/B testing, and feature performance tracking to continuously improve user experience.",
      delay: 500,
      category: "Product"
    },
    
    // Quality Assurance
    {
      icon: <BugReportIcon fontSize="large" sx={{ color: '#38b000' }} />,
      title: "Test Strategy Development",
      description: "Comprehensive testing approaches tailored to your product needs, combining manual and automated testing to ensure thorough coverage and quality.",
      delay: 600,
      category: "QA"
    },
    {
      icon: <SpeedIcon fontSize="large" sx={{ color: '#38b000' }} />,
      title: "Automated Testing Frameworks",
      description: "Implementation of robust test automation for unit, integration, and end-to-end testing that accelerates delivery while maintaining high quality standards.",
      delay: 700,
      category: "QA"
    },
    {
      icon: <VerifiedUserIcon fontSize="large" sx={{ color: '#38b000' }} />,
      title: "Security & Performance Testing",
      description: "Specialized testing services including penetration testing, load testing, and performance optimization to ensure your applications are secure and scalable.",
      delay: 800,
      category: "QA"
    },
    
    // Offshore Staffing
    {
      icon: <PeopleIcon fontSize="large" sx={{ color: '#9c27b0' }} />,
      title: "Dedicated Development Teams",
      description: "Custom-assembled offshore teams that integrate seamlessly with your organization, providing specialized skills and scaling to meet your project needs.",
      delay: 900,
      category: "Offshore"
    },
    {
      icon: <HandshakeIcon fontSize="large" sx={{ color: '#9c27b0' }} />,
      title: "Team Management & Communication",
      description: "Effective management structures and communication protocols that overcome geographic barriers and ensure alignment with your business objectives.",
      delay: 1000,
      category: "Offshore"
    },
    {
      icon: <ApartmentIcon fontSize="large" sx={{ color: '#9c27b0' }} />,
      title: "Infrastructure & Resource Optimization",
      description: "Strategic resource allocation and infrastructure management that maximizes the cost-effectiveness and efficiency of your offshore operations.",
      delay: 1100,
      category: "Offshore"
    }
  ];

  // Process steps
  const processSteps: ProcessStep[] = [
    {
      icon: <AssessmentIcon />,
      title: "Assessment & Planning",
      description: "We evaluate your current processes, systems, and team capabilities to identify opportunities for improvement and develop a tailored support strategy.",
      delay: 0
    },
    {
      icon: <SettingsSystemDaydreamIcon />,
      title: "Infrastructure Setup",
      description: "Our team establishes the necessary technical infrastructure, tools, and environments to support efficient development, testing, and deployment workflows.",
      delay: 200
    },
    {
      icon: <GroupWorkIcon />,
      title: "Team Integration",
      description: "We seamlessly integrate our support and maintenance resources with your existing teams, ensuring clear communication channels and collaboration processes.",
      delay: 400
    },
    {
      icon: <CachedIcon />,
      title: "Continuous Delivery",
      description: "Implementation of agile processes and DevOps practices that enable regular, reliable delivery of improvements and new features to your systems.",
      delay: 600
    },
    {
      icon: <SupportAgentIcon />,
      title: "Ongoing Support",
      description: "Proactive monitoring, issue resolution, and system optimization to ensure your applications maintain peak performance and reliability.",
      delay: 800
    },
    {
      icon: <AutoAwesomeIcon />,
      title: "Optimization & Innovation",
      description: "Continuous evaluation of processes and technologies to identify opportunities for innovation, automation, and performance enhancement.",
      delay: 1000
    }
  ];

  // Technologies
  const technologies: Technology[] = [
    // Agile Technologies
    { 
      name: "JIRA", 
      icon: <img src="https://wac-cdn.atlassian.com/assets/img/favicons/atlassian/apple-touch-icon.png" alt="JIRA" width="48" height="48" />,
      delay: 0,
      category: "Agile"
    },
    { 
      name: "Confluence", 
      icon: <img src="https://wac-cdn.atlassian.com/assets/img/favicons/confluence/apple-touch-icon.png" alt="Confluence" width="48" height="48" />,
      delay: 100,
      category: "Agile"
    },
    { 
      name: "Azure DevOps", 
      icon: <img src="https://cdn.vsassets.io/content/icons/favicon.ico" alt="Azure DevOps" width="48" height="48" />,
      delay: 200,
      category: "Agile"
    },
    
    // Product Management
    { 
      name: "Roadmunk", 
      icon: <img src="https://www.roadmunk.com/hubfs/Roadmunk_January2020/images/favicon.png" alt="Roadmunk" width="48" height="48" />,
      delay: 300,
      category: "Product"
    },
    { 
      name: "ProductPlan", 
      icon: <img src="https://www.productplan.com/uploads/2018/02/productplan-favicon.png" alt="ProductPlan" width="48" height="48" />,
      delay: 400,
      category: "Product"
    },
    { 
      name: "Amplitude", 
      icon: <img src="https://amplitude.com/favicon.ico" alt="Amplitude" width="48" height="48" />,
      delay: 500,
      category: "Product"
    },
    
    // QA
    { 
      name: "Selenium", 
      icon: <img src="https://www.selenium.dev/favicon.ico" alt="Selenium" width="48" height="48" />,
      delay: 600,
      category: "QA"
    },
    { 
      name: "JUnit/TestNG", 
      icon: <img src="https://junit.org/junit5/assets/img/junit5-logo.png" alt="JUnit" width="48" height="48" />,
      delay: 700,
      category: "QA"
    },
    { 
      name: "Cypress", 
      icon: <img src="https://www.cypress.io/icons/icon-48x48.png" alt="Cypress" width="48" height="48" />,
      delay: 800,
      category: "QA"
    },
    
    // Offshore
    { 
      name: "Slack", 
      icon: <img src="https://a.slack-edge.com/80588/marketing/img/meta/favicon-32.png" alt="Slack" width="48" height="48" />,
      delay: 900,
      category: "Offshore"
    },
    { 
      name: "Microsoft Teams", 
      icon: <img src="https://statics.teams.cdn.office.net/evergreen-assets/apps/teams-icon-60.cb550d70582.png" alt="Microsoft Teams" width="48" height="48" />,
      delay: 1000,
      category: "Offshore"
    },
    { 
      name: "GitHub", 
      icon: <img src="https://github.githubassets.com/favicons/favicon.png" alt="GitHub" width="48" height="48" />,
      delay: 1100,
      category: "Offshore"
    }
  ];

  // Testimonials
  const testimonials: Testimonial[] = [
    {
      id: 1,
      content: "Their Agile implementation transformed our development process. Within three months, we saw a 30% increase in delivery speed and the quality of our releases improved dramatically. The team's expertise in Scrum has been invaluable.",
      author: "Michael Chen",
      position: "CTO, FinTech Solutions",
      image: "https://randomuser.me/api/portraits/men/42.jpg",
      rating: 5
    },
    {
      id: 2,
      content: "The product management support we received helped us clarify our roadmap and prioritize features that actually drove user engagement. Our product is now much more aligned with market needs and customer feedback cycles are significantly shorter.",
      author: "Jessica Williams",
      position: "Product Director, E-commerce Platform",
      image: "https://randomuser.me/api/portraits/women/45.jpg",
      rating: 5
    },
    {
      id: 3,
      content: "The QA team they provided completely transformed our testing approach. We've reduced critical production bugs by 85% while actually accelerating our release cycle. Their automated testing framework has been a game-changer for our reliability.",
      author: "David Rodriguez",
      position: "Head of Engineering, SaaS Company",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      rating: 5
    },
    {
      id: 4,
      content: "We were skeptical about offshore staffing, but their team integration approach exceeded our expectations. The offshore developers are as productive and aligned with our goals as our in-house team, and we've been able to accelerate development while reducing costs.",
      author: "Priya Sharma",
      position: "VP of Technology, Healthcare Solutions",
      image: "https://randomuser.me/api/portraits/women/65.jpg",
      rating: 4
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const handlePrevTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0099ff0a' }}>
      <PageTitle title="Designing" />
      <Navbar />
      <HeroBox component="section" ref={sectionRefs.hero}>
        {/* Background elements */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <GradientCircle 
            sx={{ 
              top: 0, 
              right: 0, 
              width: 800, 
              height: 800, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(25%, -25%)'
            }}
          />
          <GradientCircle 
            sx={{ 
              bottom: 0, 
              left: 0, 
              width: 600, 
              height: 600, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(-33%, 33%)'
            }}
          />
          
          {/* Support Background Elements */}
          <SupportBg>
            <svg width="100%" height="100%" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
              {/* Abstract network/support visualization */}
              <g opacity="0.1" stroke="white" strokeWidth="1" fill="none">
                {/* Create network and support nodes */}
                {Array.from({ length: 20 }).map((_, i) => {
                  const x = 100 + Math.floor(Math.random() * 1000);
                  const y = 100 + Math.floor(Math.random() * 600);
                  return (
                    <g key={i}>
                      <circle cx={x} cy={y} r="10" fill="white" opacity="0.6" />
                      {Array.from({ length: 3 }).map((_, j) => {
                        const targetIndex = Math.floor(Math.random() * 20);
                        const targetX = 100 + Math.floor(Math.random() * 1000);
                        const targetY = 100 + Math.floor(Math.random() * 600);
                        return (
                          <line 
                            key={`${i}-${j}`}
                            x1={x}
                            y1={y}
                            x2={targetX}
                            y2={targetY}
                            stroke="white"
                            strokeOpacity="0.3"
                          />
                        );
                      })}
                    </g>
                  );
                })}
              </g>
              
              {/* Support ticket icons */}
              <g opacity="0.1" fill="white">
                {Array.from({ length: 5 }).map((_, i) => (
                  <g key={i} transform={`translate(${200 * i + 100}, 400)`}>
                    <rect width="150" height="120" rx="5" />
                    <line x1="20" y1="30" x2="130" y2="30" stroke="black" strokeWidth="2" />
                    <line x1="20" y1="60" x2="130" y2="60" stroke="black" strokeWidth="2" />
                    <line x1="20" y1="90" x2="130" y2="90" stroke="black" strokeWidth="2" />
                  </g>
                ))}
              </g>
              
              {/* Server/maintenance elements */}
              <g opacity="0.1" fill="white">
                <rect x="200" y="100" width="250" height="150" rx="5" />
                <rect x="220" y="120" width="210" height="20" rx="2" />
                <rect x="220" y="150" width="210" height="20" rx="2" />
                <rect x="220" y="180" width="210" height="20" rx="2" />
                
                <rect x="700" y="100" width="250" height="150" rx="5" />
                <circle cx="750" cy="175" r="30" />
                <rect x="790" y="155" width="140" height="10" rx="2" />
                <rect x="790" y="175" width="120" height="10" rx="2" />
                <rect x="790" y="195" width="130" height="10" rx="2" />
              </g>
            </svg>
          </SupportBg>
        </Box>

        <Container sx={{ position: 'relative', zIndex: 10 }}>
          <Box maxWidth="md">
            <FadeInBox visible={isVisible.hero}>
              <Typography variant="h2" component="h1" gutterBottom>
                Support &
                <Box component="span" display="block" sx={{ color: '#0099ff' }}>Maintenance</Box>
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={200}>
              <Typography variant="h5" paragraph sx={{ color: 'grey.300' }}>
                Comprehensive technical support, agile project management, quality assurance, and offshore staffing solutions to keep your systems running at peak performance
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={400} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleContactClick}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#044D76',
                  '&:hover': { 
                    bgcolor: alpha('#044D76', 0.9),
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large" 
                href="#portfolio"
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { 
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                View Our Work
              </Button>
            </FadeInBox>
          </Box>
        </Container>

        {/* Wave shape divider */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 80.5C672 71 768 35.5 864 26.625C960 17.75 1056 35.5 1152 44.375C1248 53.25 1344 53.25 1392 53.25H1440V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
              fill={theme.palette.background.default}
            />
          </svg>
        </Box>
      </HeroBox>

      {/* Overview Section */}
      <Box component="section" ref={sectionRefs.overview} py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview}>
                <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
                  Maximize Performance and Reliability
                </Typography>
                <Typography variant="body1" paragraph>
                  Our Support and Maintenance services ensure your applications and systems operate at peak efficiency while continuously evolving to meet changing business needs and technological advancements.
                </Typography>
                <Typography variant="body1" paragraph>
                  We combine expert technical support with agile project management, rigorous quality assurance, and flexible offshore staffing solutions to provide comprehensive coverage for all your technology needs.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                  {[
                    "24/7 technical support and monitoring",
                    "Agile project management methodologies",
                    "Comprehensive quality assurance",
                    "Cost-effective offshore staffing solutions"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#044D76' }} />
                      <Typography variant="body1" fontWeight="medium">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </FadeInBox>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview} delay={300}>
                <Box sx={{ position: 'relative' }}>
                  {/* Kanban Board Visualization */}
                  <KanbanBoard>
                    <KanbanHeader>
                      <Typography variant="h6" fontWeight="bold">Project Management Dashboard</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }} variant="outlined">
                          <PlayArrowIcon fontSize="small" sx={{ mr: 0.5 }} /> Sprint 24
                        </Button>
                      </Box>
                    </KanbanHeader>
                    
                    <KanbanColumns>
                      <KanbanColumn>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#044D76' }}>
                          Backlog
                        </Typography>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #ff9800' }}>
                          <Typography variant="body2" fontWeight="medium">Feature request: Export to PDF</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="Medium" sx={{ bgcolor: '#ff980033', color: '#ff9800', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-345</Typography>
                          </Box>
                        </KanbanCard>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #f44336' }}>
                          <Typography variant="body2" fontWeight="medium">Performance optimization: Dashboard</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="High" sx={{ bgcolor: '#f4433633', color: '#f44336', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-350</Typography>
                          </Box>
                        </KanbanCard>
                      </KanbanColumn>
                      
                      <KanbanColumn>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#044D76' }}>
                          In Progress
                        </Typography>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #f44336' }}>
                          <Typography variant="body2" fontWeight="medium">Fix: Authentication token refresh</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="High" sx={{ bgcolor: '#f4433633', color: '#f44336', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-342</Typography>
                          </Box>
                        </KanbanCard>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #4caf50' }}>
                          <Typography variant="body2" fontWeight="medium">Update: User notification preferences</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="Low" sx={{ bgcolor: '#4caf5033', color: '#4caf50', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-338</Typography>
                          </Box>
                        </KanbanCard>
                      </KanbanColumn>
                      
                      <KanbanColumn>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#044D76' }}>
                          QA
                        </Typography>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #ff9800' }}>
                          <Typography variant="body2" fontWeight="medium">Feature: Multi-factor authentication</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="Medium" sx={{ bgcolor: '#ff980033', color: '#ff9800', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-329</Typography>
                          </Box>
                        </KanbanCard>
                      </KanbanColumn>
                      
                      <KanbanColumn>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#044D76' }}>
                          Done
                        </Typography>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #4caf50' }}>
                          <Typography variant="body2" fontWeight="medium">Update: API documentation</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="Low" sx={{ bgcolor: '#4caf5033', color: '#4caf50', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-330</Typography>
                          </Box>
                        </KanbanCard>
                        
                        <KanbanCard sx={{ borderLeft: '4px solid #f44336' }}>
                          <Typography variant="body2" fontWeight="medium">Fix: Data import validation</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Chip size="small" label="High" sx={{ bgcolor: '#f4433633', color: '#f44336', fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="text.secondary">SP-335</Typography>
                          </Box>
                        </KanbanCard>
                      </KanbanColumn>
                    </KanbanColumns>
                  </KanbanBoard>
                  
                  {/* Background decoration */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -24, 
                      right: -24, 
                      width: 128, 
                      height: 128, 
                      borderRadius: '50%', 
                      bgcolor: '#0099ff',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -32, 
                      left: -32, 
                      width: 160, 
                      height: 160, 
                      borderRadius: '50%', 
                      bgcolor: '#044D76',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                </Box>
              </FadeInBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        component="section" 
        ref={sectionRefs.features}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.features}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our Support & Maintenance Services
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We offer comprehensive support across four key domains to ensure your systems operate reliably and your projects are delivered efficiently.
            </Typography>
          </FadeInBox>
          
          {/* Agile Project Management */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" gutterBottom sx={{ color: '#0099ff', mb: 3 }}>
              Agile Project Management
            </Typography>
            <Grid container spacing={4}>
              {featureItems.filter(item => item.category === "Agile").map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard 
                    elevation={2}
                    visible={isVisible.features}
                    delay={feature.delay}
                    category={feature.category}
                  >
                    <IconBox className="featureIcon">
                      {feature.icon}
                    </IconBox>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom
                      className="featureTitle"
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* Product Management */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" gutterBottom sx={{ color: '#044D76', mb: 3 }}>
              Product Management
            </Typography>
            <Grid container spacing={4}>
              {featureItems.filter(item => item.category === "Product").map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard 
                    elevation={2}
                    visible={isVisible.features}
                    delay={feature.delay}
                    category={feature.category}
                  >
                    <IconBox className="featureIcon">
                      {feature.icon}
                    </IconBox>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom
                      className="featureTitle"
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* Quality Assurance */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" gutterBottom sx={{ color: '#38b000', mb: 3 }}>
              Quality Assurance
            </Typography>
            <Grid container spacing={4}>
              {featureItems.filter(item => item.category === "QA").map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard 
                    elevation={2}
                    visible={isVisible.features}
                    delay={feature.delay}
                    category={feature.category}
                  >
                    <IconBox className="featureIcon">
                      {feature.icon}
                    </IconBox>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom
                      className="featureTitle"
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* Offshore Staffing */}
          <Box>
            <Typography variant="h4" component="h3" gutterBottom sx={{ color: '#9c27b0', mb: 3 }}>
              Offshore Staffing
            </Typography>
            <Grid container spacing={4}>
              {featureItems.filter(item => item.category === "Offshore").map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard 
                    elevation={2}
                    visible={isVisible.features}
                    delay={feature.delay}
                    category={feature.category}
                  >
                    <IconBox className="featureIcon">
                      {feature.icon}
                    </IconBox>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom
                      className="featureTitle"
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Box component="section" ref={sectionRefs.process} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.process}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our Support Process
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We follow a structured approach to ensure our support and maintenance services are seamlessly integrated with your operations and deliver continuous value.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <ProcessTimelineLine />
            
            {/* Process steps */}
            {processSteps.map((step, index) => (
              <ProcessStepBox 
                key={index}
                visible={isVisible.process}
                delay={step.delay}
                sx={{ 
                  flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                }}
              >
                <Box 
                  sx={{ 
                    width: '50%',
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    pr: index % 2 === 0 ? 6 : 0,
                    pl: index % 2 === 0 ? 0 : 6,
                  }}
                >
                  <Typography variant="h6" component="h3" gutterBottom sx={{ color: '#044D76' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                
                <ProcessIconBox>
                  {step.icon}
                </ProcessIconBox>
                
                <Box sx={{ width: '50%' }} />
              </ProcessStepBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Technologies Section */}
      <Box 
        component="section" 
        ref={sectionRefs.technologies}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.technologies}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Tools & Technologies
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Our teams are proficient in a wide range of tools and technologies across our four key service areas.
            </Typography>
          </FadeInBox>
          
          {/* Agile Technologies */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#0099ff', mb: 3 }}>
              Agile Project Management
            </Typography>
            <Grid container spacing={4}>
              {technologies.filter(tech => tech.category === "Agile").map((tech, index) => (
                <Grid item xs={6} sm={4} md={4} key={index}>
                  <TechCard 
                    elevation={1}
                    visible={isVisible.technologies}
                    delay={tech.delay}
                    category={tech.category}
                  >
                    <TechIconBox className="techIcon" category={tech.category}>
                      {tech.icon}
                    </TechIconBox>
                    <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                      {tech.name}
                    </Typography>
                    <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expert implementation
                    </Typography>
                  </TechCard>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* Product Management Technologies */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#044D76', mb: 3 }}>
              Product Management
            </Typography>
            <Grid container spacing={4}>
              {technologies.filter(tech => tech.category === "Product").map((tech, index) => (
                <Grid item xs={6} sm={4} md={4} key={index}>
                  <TechCard 
                    elevation={1}
                    visible={isVisible.technologies}
                    delay={tech.delay}
                    category={tech.category}
                  >
                    <TechIconBox className="techIcon" category={tech.category}>
                      {tech.icon}
                    </TechIconBox>
                    <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                      {tech.name}
                    </Typography>
                    <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expert implementation
                    </Typography>
                  </TechCard>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* QA Technologies */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#38b000', mb: 3 }}>
              Quality Assurance
            </Typography>
            <Grid container spacing={4}>
              {technologies.filter(tech => tech.category === "QA").map((tech, index) => (
                <Grid item xs={6} sm={4} md={4} key={index}>
                  <TechCard 
                    elevation={1}
                    visible={isVisible.technologies}
                    delay={tech.delay}
                    category={tech.category}
                  >
                    <TechIconBox className="techIcon" category={tech.category}>
                      {tech.icon}
                    </TechIconBox>
                    <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                      {tech.name}
                    </Typography>
                    <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expert implementation
                    </Typography>
                  </TechCard>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* Offshore Technologies */}
          <Box>
            <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#9c27b0', mb: 3 }}>
              Offshore Collaboration
            </Typography>
            <Grid container spacing={4}>
              {technologies.filter(tech => tech.category === "Offshore").map((tech, index) => (
                <Grid item xs={6} sm={4} md={4} key={index}>
                  <TechCard 
                    elevation={1}
                    visible={isVisible.technologies}
                    delay={tech.delay}
                    category={tech.category}
                  >
                    <TechIconBox className="techIcon" category={tech.category}>
                      {tech.icon}
                    </TechIconBox>
                    <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                      {tech.name}
                    </Typography>
                    <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expert implementation
                    </Typography>
                  </TechCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box component="section" ref={sectionRefs.testimonials} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.testimonials}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Client Success Stories
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover how our support and maintenance services have helped organizations improve efficiency, quality, and development velocity.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative', maxWidth: 'md', mx: 'auto' }}>
            {/* Left Navigation */}
            <TestimonialNavButton 
              onClick={handlePrevTestimonial}
              sx={{ 
                left: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
            
            {/* Testimonial Card */}
            <TestimonialCard elevation={3}>
              <Box sx={{ mb: 2 }}>
                {testimonials[currentTestimonial].id === 1 && (
                  <CategoryChip label="Agile Project Management" category="Agile" />
                )}
                {testimonials[currentTestimonial].id === 2 && (
                  <CategoryChip label="Product Management" category="Product" />
                )}
                {testimonials[currentTestimonial].id === 3 && (
                  <CategoryChip label="Quality Assurance" category="QA" />
                )}
                {testimonials[currentTestimonial].id === 4 && (
                  <CategoryChip label="Offshore Staffing" category="Offshore" />
                )}
              </Box>
              
              <Typography 
                variant="body1" 
                component="blockquote" 
                sx={{ 
                  fontStyle: 'italic', 
                  position: 'relative',
                  mb: 4,
                  '&::before': {
                    content: '"\\201C"',
                    position: 'absolute',
                    left: -30,
                    top: -20,
                    fontSize: '5rem',
                    color: 'rgba(4, 77, 118, 0.1)',
                    fontFamily: 'Georgia, serif'
                  }
                }}
              >
                "{testimonials[currentTestimonial].content}"
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Box 
                    key={index}
                    component="span"
                    sx={{ 
                      color: index < testimonials[currentTestimonial].rating ? '#0099ff' : 'rgba(0, 0, 0, 0.2)',
                      mx: 0.5,
                      fontSize: '24px',
                    }}
                  >
                    ★
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].author}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    mr: 2,
                    border: '2px solid rgba(4, 77, 118, 0.2)'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#044D76', fontWeight: 'bold' }}>
                    {testimonials[currentTestimonial].author}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonials[currentTestimonial].position}
                  </Typography>
                </Box>
              </Box>
            </TestimonialCard>
            
            {/* Right Navigation */}
            <TestimonialNavButton 
              onClick={handleNextTestimonial}
              sx={{ 
                right: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
          </Box>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box component="section" id="portfolio" py={10}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our Support Portfolio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore some of our successful support and maintenance projects and the tangible results we've delivered for our clients.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {portfolioProjects.map((project) => (
              <Grid item xs={12} sm={6} md={6} key={project.id}>
                <ProjectCard elevation={4}>
                  <Box sx={{ height: 240, position: 'relative' }}>
                    <ProjectImage 
                      src={project.image} 
                      alt={project.title}
                      className="projectImage"
                    />
                    <ProjectOverlay className="projectOverlay">
                      <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'common.white' }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ color: 'grey.200' }}>
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.tags.map((tag, i) => (
                          <Chip 
                            key={i}
                            label={tag}
                            size="small"
                            sx={{ 
                              bgcolor: project.tags[0].includes('Agile') ? 'rgba(0, 153, 255, 0.8)' :
                                      project.tags[0].includes('Software') ? 'rgba(4, 77, 118, 0.8)' :
                                      project.tags[0].includes('Quality') ? 'rgba(56, 176, 0, 0.8)' :
                                      'rgba(156, 39, 176, 0.8)',
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    </ProjectOverlay>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                bgcolor: '#044D76',
                color: 'common.white',
                '&:hover': {
                  bgcolor: alpha('#044D76', 0.9),
                  transform: 'translateY(-4px)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>
      
      <LetsTalk/>

      <Footer />
    </Box>
  );
};

export default SupportMaintenancePage;